import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import ConfirmDialog from './ConfirmDialog';
import { getCacheData, setCacheData } from '../utils/dataCache';

const AppHeader = () => {
  const { user, signOut } = useAuth();
  const { language, setLanguage, t, dir } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (userMenuOpen && !target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Fetch user credits and check admin status
  useEffect(() => {
    if (user) {
      const fetchUserData = async () => {
        try {
          // 📦 محاولة تحميل الكريدت من الـ cache أولاً
          const cacheKey = `user_credits_${user.id}`;
          const cachedCredits = getCacheData<{ credits: number; isAdmin: boolean }>(cacheKey, 30000); // 30 seconds TTL
          
          if (cachedCredits) {
            setCredits(cachedCredits.credits);
            setIsAdmin(cachedCredits.isAdmin);
          }
          
          // Get credits using RPC function
          const { data, error } = await supabase.rpc('get_user_available_credits', { 
            p_user_id: user.id 
          });
          
          if (error) {
            console.error('RPC Error:', error);
            setCredits(0);
          } else if (data !== null && data !== undefined) {
            const newCredits = Number(data) || 0;
            setCredits(newCredits);
            
            // Check if admin (get user email from auth)
            const { data: { user: authUser } } = await supabase.auth.getUser();
            const newIsAdmin = authUser?.email === 'ahmedaldakheel02@gmail.com';
            
            // 💾 حفظ في الـ cache
            setCacheData(cacheKey, { credits: newCredits, isAdmin: newIsAdmin });
          } else {
            console.warn('No credits data returned');
            setCredits(0);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setCredits(0);
        }
      };
      fetchUserData();
      
      // 🌐 إنشاء دالة global لتحديث الكريدت من أي صفحة
      (window as any).updateCreditsCache = fetchUserData;
      
      // Refresh every 30 seconds
      const interval = setInterval(fetchUserData, 30000);
      return () => {
        clearInterval(interval);
        delete (window as any).updateCreditsCache;
      };
    }
  }, [user]);

  const handleSignOut = () => {
    setShowSignOutConfirm(true);
  };

  const confirmSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const primaryNavItems = [
    { path: '/landing', labelKey: 'nav.home', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/', labelKey: 'nav.dashboard', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
    { path: '/runner', labelKey: 'nav.runner', icon: 'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664zM21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  ];

  const secondaryNavItems = [
    { path: '/dashboard', labelKey: 'nav.projects', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
    { path: '/credits', labelKey: 'nav.credits', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { path: '/custom-fonts', labelKey: 'nav.fonts', icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
  ];

  const allNavItems = [...primaryNavItems, ...secondaryNavItems];

  const getUserName = () => {
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    if (user?.email) return user.email.split('@')[0];
    return t('common.user');
  };

  const toggleLanguage = () => {
    setLanguage(language === 'ar' ? 'en' : 'ar');
  };

  return (
    <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-50" dir={dir}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
              <img 
                src="/new-logo.png" 
                alt="Audome Logo" 
                className="h-10 w-auto"
              />
            </button>
            <span className="bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded border border-blue-400">
              BETA
            </span>
          </div>

          {/* Desktop Navigation - Only show for logged in users */}
          {user && (
            <nav className="hidden md:flex items-center gap-4">
              {/* Primary Navigation (الرئيسية + رندر) */}
              <div className="flex items-center gap-1">
                {primaryNavItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-semibold ${
                      location.pathname === item.path
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:text-white hover:bg-zinc-800'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    <span className="text-sm">{t(item.labelKey)}</span>
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div className="w-px h-8 bg-zinc-700"></div>

              {/* Secondary Navigation */}
              <div className="flex items-center gap-1">
                {secondaryNavItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      location.pathname === item.path
                        ? 'bg-zinc-700 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-zinc-800'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    <span className="text-xs font-medium">{t(item.labelKey)}</span>
                  </button>
                ))}
              </div>
            </nav>
          )}

          {/* User Menu or Auth Buttons */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors text-sm font-medium text-white flex items-center gap-2"
              title={language === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
            >
              <span>{language === 'ar' ? 'EN' : 'AR'}</span>
            </button>
            
            {/* Show auth buttons for guests */}
            {!user && (
              <>
                <button
                  onClick={() => navigate('/signin')}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors font-medium"
                >
                  {t('nav.signIn')}
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-colors"
                >
                  {t('nav.signUp')}
                </button>
              </>
            )}
            
            {/* Credits Badge - Only for logged in users */}
            {user && credits !== null && (
              <button
                onClick={() => navigate('/credits')}
                className={`hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                  credits <= 0 
                    ? 'bg-red-900/30 border border-red-500/50 hover:bg-red-900/50' 
                    : credits <= 10
                    ? 'bg-orange-900/30 border border-orange-500/50 hover:bg-orange-900/50'
                    : 'bg-yellow-900/30 border border-yellow-500/50 hover:bg-yellow-900/50'
                }`}
                title={t('nav.credits')}
              >
                <svg className={`w-5 h-5 ${credits <= 0 ? 'text-red-400' : credits <= 10 ? 'text-orange-400' : 'text-yellow-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`text-sm font-bold ${credits <= 0 ? 'text-red-300' : credits <= 10 ? 'text-orange-300' : 'text-yellow-300'}`}>
                  {credits}
                </span>
              </button>
            )}
            
            {/* User Dropdown Menu - Only for logged in users */}
            {user && (
              <div className="hidden sm:block relative user-menu-container">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {getUserName().charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-sm text-white font-medium">{getUserName()}</span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {userMenuOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden z-50">
                  <button
                    onClick={() => {
                      navigate('/account');
                      setUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-700 transition-colors text-right"
                  >
                    <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-white font-medium">{t('nav.account')}</span>
                  </button>

                  {user?.email === 'ahmedaldakheel02@gmail.com' && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setUserMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-700 transition-colors text-right border-t border-zinc-700"
                    >
                      <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span className="text-white font-medium">🔐 {t('nav.admin')}</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      handleSignOut();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-900/30 transition-colors text-right border-t border-zinc-700"
                  >
                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="text-white font-medium">{t('nav.signOut')}</span>
                  </button>
                </div>
              )}
              </div>
            )}
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-400 hover:text-white transition-colors p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-zinc-800 animate-slideDown">
            {/* Credits Display in Mobile Menu */}
            {credits !== null && (
              <div className="mx-4 mb-4 bg-gradient-to-r from-yellow-900/40 to-yellow-800/40 border border-yellow-500/50 rounded-lg p-4 text-center animate-fadeIn">
                <div className="text-yellow-400 text-xs font-semibold mb-1">{t('nav.remainingCredits')}</div>
                <div className="text-yellow-200 text-3xl font-bold">{credits}</div>
              </div>
            )}
            
            <nav className="flex flex-col gap-2">
              {allNavItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === item.path
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                  </svg>
                  <span className="font-medium">{t(item.labelKey)}</span>
                </button>
              ))}
              
              {/* Divider */}
              <div className="border-t border-zinc-700 my-2"></div>
              
              {/* Account Button */}
              <button
                onClick={() => {
                  navigate('/account');
                  setMobileMenuOpen(false);
                }}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === '/account'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="font-medium">{t('nav.account')}</span>
              </button>
              
              {/* Admin Button - Only for Admin */}
              {isAdmin && (
                <button
                  onClick={() => {
                    navigate('/admin');
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === '/admin'
                      ? 'bg-purple-600 text-white'
                      : 'text-purple-400 hover:text-purple-300 hover:bg-purple-900/20'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="font-medium">🔐 {t('nav.admin')}</span>
                </button>
              )}
              
              {/* Sign Out Button */}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleSignOut();
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-red-400 hover:text-red-300 hover:bg-red-900/20"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="font-medium">{t('nav.signOut')}</span>
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Sign Out Confirmation */}
      <ConfirmDialog
        isOpen={showSignOutConfirm}
        onClose={() => setShowSignOutConfirm(false)}
        onConfirm={confirmSignOut}
        title={t('nav.signOutConfirm.title')}
        message={t('nav.signOutConfirm.message')}
        confirmText={t('nav.signOutConfirm.confirm')}
        cancelText={t('common.cancel')}
        type="warning"
        icon={
          <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </div>
        }
      />
    </header>
  );
};

export default AppHeader;

